import React from 'react';
import { Target, ListChecks, ChevronRight, Trophy, Calendar } from 'lucide-react';
import { useI18n } from '../i18n';

const ORANGE = '#f59e0b';
const GREEN = '#22c55e';
const BLUE = '#3b82f6';
const YELLOW = '#eab308';
const RED = '#ef4444';

function Gauge({ score = 53 }) {
  const cx = 110, cy = 112, r = 88;
  const f = Math.max(0, Math.min(1, score / 100));
  const theta = Math.PI - f * Math.PI;
  const px = cx + r * Math.cos(theta);
  const py = cy - r * Math.sin(theta);
  const track = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const fg = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${px} ${py}`;
  return (
    <div style={{ position: 'relative', width: 220, margin: '0 auto' }}>
      <svg width="220" height="132" viewBox="0 0 220 132">
        <defs>
          <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={ORANGE} />
            <stop offset="1" stopColor={GREEN} />
          </linearGradient>
        </defs>
        <path d={track} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="13" strokeLinecap="round" />
        <path d={fg} fill="none" stroke="url(#gaugeGrad)" strokeWidth="13" strokeLinecap="round" />
        <circle cx={px} cy={py} r="7" fill="#0a0a0c" stroke="#fff" strokeWidth="2" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, top: 34, textAlign: 'center' }}>
        <div className="font-sora" style={{ fontSize: 58, fontWeight: 800, color: ORANGE, lineHeight: 1 }}>{score}</div>
        <div style={{ marginTop: 4, fontSize: 12, letterSpacing: '0.22em', color: 'var(--color-neutral-500)', fontWeight: 600 }}>HITMAX SCORE</div>
      </div>
    </div>
  );
}

function ScenarioRow({ label, pct, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
      <span style={{ width: 74, flex: '0 0 auto', fontSize: 15, color: 'var(--color-text)' }}>{label}</span>
      <div style={{ flex: 1, height: 12, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 999 }} />
      </div>
      <span style={{ width: 46, textAlign: 'right', fontWeight: 800, fontSize: 16 }}>{pct}%</span>
    </div>
  );
}

function OddPill({ label, value, color }) {
  return (
    <div style={{ flex: 1, borderRadius: 14, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderTop: `2px solid ${color}`, padding: '12px 8px', textAlign: 'center' }}>
      <div style={{ fontSize: 13, color, fontWeight: 600 }}>{label}</div>
      <div className="font-sora" style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

export default function FeaturedMatch({ onConnect }) {
  const { t } = useI18n();
  const m = t.match;
  return (
    <section id="match" style={{ padding: '44px 24px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <p style={{ textAlign: 'center', fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--hm-gold)', margin: 0 }}>{m.eyebrow}</p>
        <h2 className="font-sora" style={{ textAlign: 'center', margin: '10px 0 0', fontSize: 'clamp(28px, 4vw, 46px)', fontWeight: 800, letterSpacing: '-0.02em' }}>{m.title}</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, justifyContent: 'center', marginTop: 14, color: 'var(--color-neutral-400)', fontSize: 13.5 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Trophy size={15} color="var(--hm-gold)" /> {m.competition}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Calendar size={15} color="var(--hm-gold)" /> {m.date}</span>
        </div>

        <div style={{ marginTop: 26, borderRadius: 20, background: 'linear-gradient(180deg,#0b0c10,#08090c)', border: '1px solid rgba(255,255,255,0.08)', padding: '26px 22px', boxShadow: 'var(--shadow-lg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.16em', color: 'var(--color-neutral-500)' }}>{m.prediction}</span>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', color: ORANGE }}>{m.tendency}</span>
          </div>

          <div style={{ marginTop: 14 }}><Gauge score={53} /></div>

          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, border: `1px solid ${ORANGE}55`, background: 'rgba(245,158,11,0.06)' }}>
            <Target size={22} color={ORANGE} style={{ flex: '0 0 auto' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, letterSpacing: '0.12em', color: 'var(--color-neutral-500)' }}>{m.recommendation}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{m.recoTeam}</div>
            </div>
            <div className="font-sora" style={{ fontSize: 30, fontWeight: 800, color: ORANGE }}>41%</div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, fontSize: 15, color: 'var(--color-neutral-400)' }}>
            <span>{m.altPrefix} <strong style={{ color: 'var(--color-text)' }}>{m.altValue}</strong> (66%)</span>
            <span>{m.probableScorePrefix} <strong style={{ color: 'var(--hm-gold)' }}>2 - 1</strong> (8.4%)</span>
          </div>

          <div style={{ marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--color-neutral-500)', marginBottom: 16 }}>{m.scenarios}</div>
            <ScenarioRow label="Real Madrid" pct={41} color={BLUE} />
            <ScenarioRow label={m.draw} pct={23} color={YELLOW} />
            <ScenarioRow label="Inter" pct={37} color={RED} />
          </div>

          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--color-neutral-500)', marginBottom: 12 }}>{m.marketOdds}</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <OddPill label="1" value="1.59" color={BLUE} />
              <OddPill label="N" value="4.49" color={YELLOW} />
              <OddPill label="2" value="5.22" color={RED} />
            </div>
            <div style={{ marginTop: 10, textAlign: 'center', fontSize: 12.5, color: 'var(--color-neutral-600)' }}>{m.oddsNote}</div>
          </div>

          <div style={{ marginTop: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--color-neutral-500)', marginBottom: 12 }}><ListChecks size={16} color="var(--hm-gold)" /> {m.whyScore}</div>
            {m.reasons.map((tx, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10, fontSize: 14.5, lineHeight: 1.5, color: 'var(--color-neutral-400)' }}>
                <ChevronRight size={16} color="var(--hm-gold)" style={{ flex: '0 0 auto', marginTop: 2 }} />{tx}
              </div>
            ))}
          </div>

          <button onClick={onConnect} className="btn-gold" style={{ marginTop: 22, width: '100%', padding: '14px', borderRadius: 12, fontSize: 15 }}>{m.cta}</button>
        </div>
      </div>
    </section>
  );
}
